import { useState } from "react";
import Select from "react-select";
import { NXDownArrow } from "../../icons";
import enumChoice from "../../enum";
import PaymentForm from "./PaymentForm";
import FixedDateForm from "./fixedDateForm";
import useApi from "../../api";
import { toast } from "react-toastify";

import OrderPrintButton from "./orderPrintButton";

import { aggresiveToastError } from "../common/utils";
interface ActionButtonsProps {
  data: any;
  refetch: () => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({ data, refetch }) => {
  const [isOpenPaymentModal, setIsOpenPaymentModal] = useState<boolean>(false);
  const [isOpenFixedDateModal, setIsOpenFixedDateModal] = useState<boolean>(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>();

  const api = useApi();

  const markAsPaidOptions = [
    { value: "CASH_ON_DELIVERY", label: "Cash on Delivery" },
    { value: "Cash on Delivery (Third Party)", label: "Cash on Delivery (Third Party)" },
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "BANK_TRANSFER_INSTANT", label: "Bank Transfer Instant" },
    { value: "GIFT_CARD", label: "Gift Card" },
    { value: "CHEQUE", label: "Cheque" },
    { value: "MFS", label: "MFS" },
    { value: "OTHER", label: "Other" },
  ];


  const handleStatusChange = (status: string) => {
    const payload = { status };
    api.updateOrderStatus(data?.alias, payload).then(() => {
      toast.success("Order status updated successfully");
      refetch();
    }, (error) => {
      aggresiveToastError(error);
    });
  };

  const handleMarkAsPaidChange = (selectedOption: any) => {
    if (selectedOption) {
      setSelectedPaymentMethod(selectedOption?.value);
      setIsOpenPaymentModal(!isOpenPaymentModal);
    }
  };

  const handlePaymentTermChange = (selectedOption: any) => {
    if (selectedOption) {
      const payload: { date_within?: string | number; payment_term?: string } = {};

      if (selectedOption.value === enumChoice.paymentTerms.toKV().FIXED_DATE.value) {
        setIsOpenFixedDateModal(!isOpenFixedDateModal);
        return;
      }

      if (/^\d+$/.test(selectedOption.value)) {
        payload["date_within"] = selectedOption.value;
        payload["payment_term"] = enumChoice.paymentTerms.toKV().FIXED_DATE.value;
      } else {
        payload["payment_term"] = selectedOption.value;
      }

      api.updateOrderPaymentTerm(data?.alias, payload).then((response) => {
        toast.success("Payment term updated successfully");
        refetch();
      }, (error) => {});
    }
  };

  const handlePaymentMethodChange = (e: any) => {
    const payload = { preferredPaymentMethod: e.value };
    api.updateOrderPaymentMethod(data?.alias, payload).then(() => {
      toast.success("Payment method updated successfully");
      refetch();
    }, (error) => {
      toast.error(error.response.data?.non_field_errors || error.response.data?.detail);
    });
  };

  // Custom styles for Select component
  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      width: "100%",
      minWidth: "150px",
      backgroundColor: "#f3f4f6",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#9ca3af",
      },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#3b82f6" : "white",
      color: state.isSelected ? "white" : "#1f2937",
      "&:hover": {
        backgroundColor: "#3b82f6",
        color: "white",
      },
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: "#6b7280",
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: "#6b7280",
      "&:hover": {
        color: "#1f2937",
      },
    }),
  };

  return (
    <div className="flex flex-col space-y-3 md:flex-row md:space-y-0 md:space-x-3 items-center">
      {data?.chargeStatus === enumChoice.chargeStatus.toKV().DUE.value && (
        <Select
          options={markAsPaidOptions}
          styles={customSelectStyles}
          onChange={handleMarkAsPaidChange}
          placeholder="Mark As Paid"
          isSearchable={false}
          components={{ DropdownIndicator: () => <NXDownArrow className="w-4 h-4 mr-2" /> }}
        />
      )}
      {data?.chargeStatus === enumChoice.chargeStatus.toKV().DUE.value && (
        <Select
          options={enumChoice.paymentTerms}
          styles={customSelectStyles}
          onChange={handlePaymentTermChange}
          placeholder="Payment Term"
          isSearchable={false}
          components={{ DropdownIndicator: () => <NXDownArrow className="w-4 h-4 mr-2" /> }}
        />
      )}
      {data?.paymentMethod === enumChoice?.paymentMethods.toKV().AWAITING_SELECTION.value && (
        <Select
          options={enumChoice.paymentMethods}
          styles={customSelectStyles}
          placeholder="Choose Payment Method"
          isSearchable={false}
          onChange={handlePaymentMethodChange}
          defaultValue={enumChoice.paymentMethods.find((item) => item.value === data?.preferredPaymentMethod)}
          components={{ DropdownIndicator: () => <NXDownArrow className="w-4 h-4 mr-2" /> }}
        />
      )}

      <button
        className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed md:w-auto w-full"
        onClick={(e) => { e.preventDefault(); handleStatusChange(enumChoice.paymentStatus.toKV().REFUNDED.value); }}
        disabled={
          !(
            (data?.status === enumChoice.orderStatus.toKV().RETURNED.value && data?.chargeStatus !== enumChoice.chargeStatus.toKV().DUE.value) ||
            (data?.status === enumChoice.orderStatus.toKV().CANCELLED.value && data?.chargeStatus !== enumChoice.chargeStatus.toKV().DUE.value)
          )
        }
      >
        Refund
      </button>

      {data?.status === enumChoice.orderStatus.toKV().SHIPPED.value && (
        <button
          className="bg-green-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200 md:w-auto w-full"
          onClick={(e) => { e.preventDefault(); handleStatusChange(enumChoice.orderStatus.toKV().DELIVERED.value); }}
        >
          Delivered
        </button>
      )}

      {data?.status === enumChoice.orderStatus.toKV().PACKED.value && (
        <button
          className="bg-purple-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-purple-700 transition duration-200 md:w-auto w-full"
          onClick={() => handleStatusChange(enumChoice.orderStatus.toKV().SHIPPED.value)}
        >
          Shipped
        </button>
      )}

      {(data?.status === enumChoice.orderStatus.toKV().APPROVED.value || data?.status === enumChoice.orderStatus.toKV().PACKED.value || data?.status === enumChoice.orderStatus.toKV().PENDING.value) && (
        <button
          className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-red-700 transition duration-200 md:w-auto w-full"
          onClick={() => handleStatusChange(enumChoice.orderStatus.toKV().CANCELLED.value)}
        >
          Cancel
        </button>
      )}

      {data?.status === enumChoice.orderStatus.toKV().PENDING.value && (
        <button
          className="bg-yellow-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-yellow-700 transition duration-200 md:w-auto w-full"
          onClick={() => handleStatusChange(enumChoice.orderStatus.toKV().APPROVED.value)}
        >
          Approve
        </button>
      )}

      {data?.status === enumChoice.orderStatus.toKV().APPROVED.value && (
        <button
          className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-indigo-700 transition duration-200 md:w-auto w-full"
          onClick={() => handleStatusChange(enumChoice.orderStatus.toKV().PACKED.value)}
        >
          Packed
        </button>
      )}

      
        <OrderPrintButton order_id={data?.dbId} />
    


      {/* EXTENRAL COMPONENTS */}

     {isOpenPaymentModal && <PaymentForm
        order={data}
        payment_method={selectedPaymentMethod}
        isOpen={isOpenPaymentModal}
        onClose={() => setIsOpenPaymentModal(!isOpenPaymentModal)}
        fetchOrderDetails={refetch}
      />} 
      <FixedDateForm
        order={data}
        isOpen={isOpenFixedDateModal}
        onClose={() => setIsOpenFixedDateModal(!isOpenFixedDateModal)}
        fetchOrderDetails={refetch}
      />
    </div>
  );
};

export default ActionButtons;
